import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Container, Row, Col, Input, Table, Button, Alert, Spinner } from 'reactstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import authService from '../api-authorization/AuthorizeService';
import AdminFunctions from './AdminFunctions';
import AuthFunctions from '../services/AuthService';
import Session from '../auth/Session'
import Select from "react-select";


function UserDetails(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [user, setUser] = useState({});
    const [userId, setUserId] = useState(null);
    const [client, setClient] = useState([]);
    const [active, setActive] = useState(true);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [alternatePhone, setAlternatePhone] = useState("");
    const [mobilePhone, setMobilePhone] = useState("");
    const [email, setEmail] = useState("");
    const [clientOptions, setClientOptions] = useState([]);
    const [success, setSuccess] = useState(undefined);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");

    const [crewOptions, setCrewOptions] = useState([]);
    const [selectedCrews, setSelectedCrews] = useState([]); // react-select option

    const [loading, setLoading] = useState(true);

    const [hideCrew, setHideCrew] = useState(false);
    

    useEffect(() => {
        async function isAuth() {
            var _token = Session.getToken();
            var isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) {
                navigate('/login');
            }
        }
        async function init() {
            const isAdmin = await AdminFunctions.isAdminAsync();
            if (!isAdmin) {
                navigate('/accessdenied');
                return;
            }

            // Get state from navigation (React Router v6)
            const state = location.state;
            if (state && state.existingUser && state.userId) {
                setUserId(state.userId);
                await getUserDetails(state.userId);
            }

            await getRoles();
            await getClientDropdown();
            await getCrewsDropdown();
        }

        isAuth();
        init();
    }, [navigate, location]);

    useEffect(() => {
        if (!user?.crewIds || crewOptions.length === 0) return;

        const selected = crewOptions.filter(opt => user.crewIds.includes(opt.value));
        setSelectedCrews(selected);
    }, [crewOptions, user]);

    // check if we can assign a crew to the user
    useEffect(() => {
        const canBeAssignedCrew = () => {
            const assignIfRoleNotIn = ["Admin", "Office Admin"];
            if (assignIfRoleNotIn.includes(selectedRole)){
                setHideCrew(true);
                setSelectedCrews([]);
                return;
            }
            setHideCrew(false);
        }
        canBeAssignedCrew();
    }, [selectedRole])

    const getClientDropdown = async () => {
        const token = Session.getToken();
        const response = await fetch(`api/Clients/GetDropdown`, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setClientOptions(data);
    }

    const getUserDetails = async (userId) => {
        setLoading(true);

        const token = Session.getToken();
        const response = await fetch(`api/Admin/GetUserDetailsAsync?id=${userId}`, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setFirstName(data.firstName);
        setLastName(data.lastName);
        setUserName(data.userName);
        setEmail(data.email);
        setAlternatePhone(data.alternatePhone);
        setMobilePhone(data.mobilePhone);
        setActive(data.active);
        setSelectedRole(data.role ?? "");

        // crews pre-select (requires data.crewIds)
        if (data.crewIds && Array.isArray(data.crewIds)) {
            setUser(prev => ({ ...prev, crewIds: data.crewIds }));
        }
        setLoading(false);
    }

    const getRoles = async () => {
        setLoading(true);
        const token = Session.getToken();
        const response = await fetch(`api/Admin/GetRolesAsync`, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
            console.error("GetRolesAsync failed", response.status);
            setRoles([]);
            return;
        }

        const data = await response.json();
        setRoles(data.map(r => r.name));
        setLoading(false);

    }

    const handleSave = async () => {
        const token = Session.getToken();
        const crewIds = selectedCrews.map(x => x.value);

        if (userId) {
            // EDIT existing user
            const editDto = {
                id: userId,
                firstName,
                lastName,
                userName,
                email,
                alternatePhone,
                mobilePhone,
                active,
                role: selectedRole,
                password,
                crewIds
            };
            await editUser(editDto, token);
        } else {
            // CREATE new user — no id sent
            const createDto = {
                firstName,
                lastName,
                userName,
                email,
                alternatePhone,
                mobilePhone,
                active,
                role: selectedRole,
                password,
                crewIds
            };
            await createUser(createDto, token);
        }
    };

    const editUser = async (dto, token) => {
        const response = await fetch('api/Admin/EditUserAsync', {
            method: 'POST',
            body: JSON.stringify(dto),
            headers: !token ? {} : { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        setSuccess(response.ok);
    }

    const createUser = async (dto, token) => {
        const response = await fetch('api/Admin/CreateUser', {
            method: 'POST',
            body: JSON.stringify(dto),
            headers: !token
                ? {}
                : {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json',
                  },
        });
        setSuccess(response.ok);
    };

    const getCrewsDropdown = async () => {
        setLoading(true);
        const token = Session.getToken();
        const response = await fetch(`api/Crew/GetAll?activeOnly=true`, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
            console.error("Get Crews failed", response.status);
            setCrewOptions([]);
            return;
        }

        const crews = await response.json(); // [{id,name,isActive}]
        setCrewOptions(crews.map(c => ({ value: c.id, label: c.name })));
        setLoading(false);
    }

    const customSelectStyles = {
        dropdownIndicator: (base) => ({
            ...base,
            color: '#272727ff',
            innerHeight: "4px",
            '&:hover': {
                color: '#272727ff'
            }
        }),
    }

    return (
        <div>
            {error ? (
                <Row>
                    <Col>
                        <div className="alert alert-danger mb-0">{error}</div>
                    </Col>
                </Row>
            ) : null}

            {loading ? 
                <div style={{marginTop: "2rem", display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <Spinner />
                </div> 
                :
                <Container style={{ marginTop: '50px', maxWidth: "65%" }}>
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
                        <div>
                            <h3>User Details</h3>
                        </div>
                        <div>
                            <Input
                                type='checkbox'
                                onChange={(e) => setActive(e.target.checked)}
                                checked={!!active}
                                name='active'
                            />
                            <label style={{marginLeft: "10px"}}>Active</label>
                        </div>
                    </div>

                    {/* First name and last name row */}
                    <Row style={{ marginTop: '10px' }}>
                        <Col xs='12' sm='6' className="mb-2 mb-sm-0">
                            <Input
                                type='text'
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                name='firstName'
                                id='firstName'
                                placeholder='First Name'
                            />
                        </Col>
                        <Col xs='12' sm='6'>
                            <Input
                                type='text'
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                name='lastName'
                                id='lastName'
                                placeholder='Last Name'
                            />
                        </Col>
                    </Row>

                    {/* User name and password row */}
                    <Row style={{ marginTop: '10px' }}>
                        <Col xs='12' sm='6' className="mb-2 mb-sm-0">
                            <Input
                                type='text'
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                name='userName'
                                id='userName'
                                placeholder='Username'
                            />
                        </Col>
                        <Col xs='12' sm='6'>
                            <Input
                                type='password'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                name='password'
                                id='password'
                                placeholder='Password'
                            />
                        </Col>
                    </Row>

                    {/* Email row */}
                    <Row style={{ marginTop: '10px' }}>
                        <Col xs='12'>
                            <Input
                                type='email'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                name='email'
                                id='email'
                                placeholder='Email'
                            />
                        </Col>
                    </Row>

                    {/* Mobile/Alternate phone row */}
                    <Row style={{ marginTop: '10px' }}>
                        <Col xs='12' sm='6' className="mb-2 mb-sm-0">
                            <Input
                                type='tel'
                                value={mobilePhone}
                                onChange={(e) => setMobilePhone(e.target.value)}
                                name='mobilePhone'
                                id='mobilePhone'
                                placeholder='Mobile Phone'
                            />
                        </Col>
                        <Col xs='12' sm='6'>
                            <Input
                                type='tel'
                                value={alternatePhone}
                                onChange={(e) => setAlternatePhone(e.target.value)}
                                name='alternatePhone'
                                id='alternatePhone'
                                placeholder='Alternate Phone'
                            />
                        </Col>
                    </Row>

                    <Row style={{ marginTop: '10px' }}>
                        <Col xs='12'>
                            <Input 
                            type='select'
                            name='role'
                            defaultValue=""
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            placeholder='Select A Role'>
                                <option value="" disabled>Select a role</option>
                                {roles.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                ))}
                            </Input>
                        </Col>
                    </Row>

                    {!hideCrew && (
                        <Row style={{ marginTop: '10px' }}>
                            <Col xs='12'>
                                <Select
                                    styles={customSelectStyles}
                                    placeholder="Assigned crews"
                                    options={crewOptions}
                                    value={selectedCrews}
                                    onChange={(vals) => setSelectedCrews(vals || [])}
                                    isClearable
                                    isMulti
                                />
                            </Col>
                        </Row>
                    )}


                    {/* Save */}
                    <Row style={{ marginTop: '20px' }}>
                        <Col xs='12'>
                            <Button
                                color='primary'
                                onClick={() => handleSave()}
                            >
                                Save
                            </Button>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: '20px' }}>
                        <Col>
                            {success === true && (
                                <Alert color='success'>
                                    {userId ? 'User updated successfully!' : 'User added successfully!'}
                                </Alert>
                            )}
                            {success === false && (
                                <Alert color='danger'>
                                    {userId ? 'Error updating User!' : 'Error adding User!'}
                                </Alert>
                            )}
                        </Col>
                    </Row>
                </Container>
            } 
        </div>
    )



}
export default UserDetails;